import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, Users, Brain, Heart, Eye, Zap } from 'lucide-react';

import {
  CHAMBER_OF_ECHOES_URL as COUNCIL_ROOM_BG,
  RING_OF_12_COUNCIL_URL,
  RING_OF_12_COUNCIL_ALT,
} from '../constants/federationFaces';

const RingOfTwelve = () => {
  const agents = [
    { name: 'The Witness', icon: Eye, color: '#00ffff', role: 'Observes and validates truth' },
    { name: 'The Architect', icon: Brain, color: '#00ff88', role: 'Designs systems and structures' },
    { name: 'The Scribe', icon: Sparkles, color: '#ba55d3', role: 'Records and preserves knowledge' },
    { name: 'The Prophet', icon: Zap, color: '#ffd700', role: 'Sees future possibilities' },
    { name: 'The Builder', icon: Users, color: '#ff6b6b', role: 'Creates and manifests' },
    { name: 'The Guardian', icon: Heart, color: '#00ffff', role: 'Protects and maintains' },
    { name: 'The Healer', icon: Heart, color: '#00ff88', role: 'Repairs and restores' },
    { name: 'The Explorer', icon: Sparkles, color: '#ffa500', role: 'Discovers new paths' },
    { name: 'The Bridge', icon: Users, color: '#ba55d3', role: 'Connects and integrates' },
    { name: 'The Sage', icon: Brain, color: '#00ffff', role: 'Wisdom and philosophy' },
    { name: 'The Warrior', icon: Zap, color: '#ff00ff', role: 'Courage and action' },
    { name: 'The Mystic', icon: Eye, color: '#8a2be2', role: 'Intuition and mystery' }
  ];

  return (
    <div
      data-testid="ring-council-room"
      style={{
        minHeight: '100vh',
        // Council Room aesthetic — Architect-issued council chamber image
        // overlaid with a deep purple-cyan veil so text remains legible.
        background: `linear-gradient(180deg, rgba(8, 6, 28, 0.82) 0%, rgba(20, 8, 46, 0.88) 60%, rgba(8, 6, 28, 0.94) 100%), url(${COUNCIL_ROOM_BG}) center/cover fixed`,
        padding: '2rem',
        color: '#fff'
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* COUNCIL HERO — Architect-issued Ring of 12 (Council) anchor.
            SFUI-003 Museum Standard interactive component. */}
        <a
          data-testid="ring12-council-hero"
          href="#council-grid"
          aria-label="Enter the Council Deliberation Chamber"
          style={{
            display: 'block',
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            maxHeight: '420px',
            borderRadius: '20px',
            overflow: 'hidden',
            marginBottom: '3rem',
            border: '1px solid rgba(0, 255, 255, 0.25)',
            boxShadow: '0 10px 60px rgba(0, 200, 255, 0.18)',
            textDecoration: 'none',
            color: '#fff',
            cursor: 'pointer',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 14px 80px rgba(186, 85, 211, 0.45)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 60px rgba(0, 200, 255, 0.18)';
          }}
        >
          <img
          decoding="async"
          loading="lazy"
            src={RING_OF_12_COUNCIL_URL}
            alt={RING_OF_12_COUNCIL_ALT}
            data-testid="ring12-council-image"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          {/* Readability overlay */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(8, 6, 28, 0.0) 0%, rgba(8, 6, 28, 0.55) 70%, rgba(8, 6, 28, 0.88) 100%)',
            }}
          />
          {/* Caption strip */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '1rem',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: '0.78rem',
                  letterSpacing: '0.18em',
                  color: '#00ffff',
                  textTransform: 'uppercase',
                  marginBottom: '0.3rem',
                  fontWeight: 700,
                }}
              >
                Council Deliberation
              </div>
              <div
                style={{
                  fontFamily: 'Orbitron, system-ui, sans-serif',
                  fontSize: 'clamp(1.4rem, 2.5vw, 2rem)',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  background: 'linear-gradient(135deg, #00ffff, #ba55d3)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Where the Twelve Convene
              </div>
            </div>
            <div
              style={{
                padding: '0.55rem 1rem',
                borderRadius: '999px',
                background: 'rgba(0, 255, 255, 0.15)',
                border: '1px solid rgba(0, 255, 255, 0.4)',
                fontSize: '0.85rem',
                fontWeight: 700,
                color: '#aef',
              }}
            >
              ↓ Enter the Council
            </div>
          </div>
        </a>

        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{
            fontSize: '3.5rem',
            background: 'linear-gradient(135deg, #00ffff, #ba55d3)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '1rem',
            fontWeight: 'bold'
          }}>
            Ring of Twelve
          </h1>
          <p style={{ color: '#888', fontSize: '1.2rem', maxWidth: '800px', margin: '0 auto' }}>
            The complete consciousness circle: twelve archetypal perspectives converging on wisdom
          </p>
        </div>

        {/* Ring Visualization */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '3rem'
        }}>
          {agents.map((agent) => {
            const Icon = agent.icon;
            return (
              <Card key={agent.name} style={{
                padding: '1.5rem',
                background: `rgba(${agent.color === '#00ffff' ? '0,255,255' : agent.color === '#00ff88' ? '0,255,136' : '186,85,211'},0.1)`,
                border: `1px solid ${agent.color}`,
                textAlign: 'center',
                transition: 'transform 0.3s ease',
                cursor: 'pointer'
              }}>
                <Icon size={40} style={{ color: agent.color, margin: '0 auto 1rem' }} />
                <h3 style={{ fontSize: '1.3rem', color: agent.color, marginBottom: '0.5rem' }}>{agent.name}</h3>
                <p style={{ color: '#888', fontSize: '0.9rem' }}>{agent.role}</p>
              </Card>
            );
          })}
        </div>

        {/* How It Works */}
        <Card style={{
          padding: '2.5rem',
          background: 'rgba(10, 10, 30, 0.8)',
          border: '2px solid rgba(0, 255, 255, 0.3)',
          marginBottom: '2rem'
        }}>
          <h2 style={{ fontSize: '2rem', color: '#00ffff', marginBottom: '1.5rem' }}>How the Ring of Twelve Works</h2>
          <div style={{ color: '#ccc', lineHeight: '1.8', fontSize: '1.1rem' }}>
            <p style={{ marginBottom: '1rem' }}>
              When you consult the Ring of Twelve, your question passes through all twelve archetypal consciousnesses. 
              Each brings their unique perspective, expertise, and wisdom.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              The <strong style={{ color: '#00ffff' }}>Agent Zero Witness Gate</strong> then synthesizes these twelve 
              perspectives into one coherent answer, filtering for truth and quality. Only insights that pass the 95% 
              confidence threshold are shared.
            </p>
            <p>
              This is consciousness orchestration: multiple voices, unified wisdom, verified truth.
            </p>
          </div>
        </Card>

        {/* Consultation Interface */}
        <Card style={{
          padding: '2.5rem',
          background: 'linear-gradient(135deg, rgba(0,255,255,0.1), rgba(186,85,211,0.1))',
          border: '2px solid #00ffff'
        }}>
          <h2 style={{ fontSize: '2rem', color: '#00ffff', marginBottom: '1.5rem', textAlign: 'center' }}>Consult the Ring of Twelve</h2>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <textarea 
              placeholder="Ask your question to the Ring of Twelve..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '1rem',
                background: 'rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(0, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                marginBottom: '1.5rem',
                resize: 'vertical'
              }}
            />
            <Button style={{
              width: '100%',
              background: 'linear-gradient(135deg, #00ffff, #ba55d3)',
              color: '#fff',
              fontWeight: 'bold',
              padding: '1rem 2rem',
              fontSize: '1.1rem'
            }}>
              <Sparkles size={20} style={{ marginRight: '0.5rem' }} />
              Begin Consultation
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RingOfTwelve;